import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { AssetEventRecordType, AssetType, POST_ASSET_EVENT_NAME } from '@bingads-webui-campaign-react/copilot-common';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import classNames from 'classnames';
import { getImageSuggestionCarouselStyles } from './style';
import { getCarouselStyles, getFullyShownImagesCount, initialImageContainerLeftMargin } from './carousel-pmax-style';
import { ImageRecommendationsPanel } from './image-recommendations-panel';

export const SuggestionViewImageCount = 5;
const MaxSuggestionsInCarousel = 15;
const isEmptySuggestionsHintEnabled = true;

const PerfMaxImageSuggestionCarouselInternal = (props) => {
  const {
    i18n,
    addImageCallback = _.noop,
    maxUploadNum,
    isCopilotRecommendations,
    customMessageBarText,
  } = props;

  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const {
    themes, imageAssets, postAssetEventLog = _.noop, copilotTracking,
  } = recommendationContext;
  const { carouselState, panelState } = useMemo(() => {
    if (isCopilotRecommendations) {
      return {
        carouselState: {
          carousel: {
            isError: _.isEmpty(imageAssets),
          },
          suggestion: {
            images: imageAssets,
          },
        },
        panelState: {
          panelImages: imageAssets,
          hasMore: false,
        },
      };
    }
    return (recommendationContext && recommendationContext.images) || {};
  }, [imageAssets, isCopilotRecommendations, recommendationContext]);
  const { suggestion, carousel } = carouselState || {};
  const { images: suggestionImages, inProgress: suggestionInProgress } = suggestion || {};

  const [imagesToSuggest, setImagesToSuggest] = useState([]);
  const [carouselErrorMessage, setCarouselErrorMessage] = useState({});
  const [blockCarousel, setBlockCarousel] = useState(false);
  const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);

  const logCopilotViewedImageSuggestions = useCallback((suggestionIds) => {
    if (copilotTracking) {
      const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(suggestionIds);
      if (viewedImageSuggestionIds.length > 0) {
        postAssetEventLog({
          EventName: POST_ASSET_EVENT_NAME,
          EventValue: {
            AssetType: _.map(viewedImageSuggestionIds, () => AssetType.IMAGE),
            SuggestionIds: viewedImageSuggestionIds,
            RecordType: AssetEventRecordType.VIEWED,
          },
        });
        copilotTracking.trackViewedSuggestionIds(viewedImageSuggestionIds);
      }
    }
  }, [copilotTracking, postAssetEventLog]);

  if (isSuggestionsPanelOpen) {
    const panelViewedSuggestionIds = _.pluck(panelState.panelImages, 'suggestionId');
    reportRecommendations({ view: panelViewedSuggestionIds });
    if (isCopilotRecommendations) {
      logCopilotViewedImageSuggestions(panelViewedSuggestionIds);
    }
  }

  useEffect(() => {
    const truncatedImages = _.isArray(suggestionImages) && suggestionImages.slice(0, MaxSuggestionsInCarousel);
    setImagesToSuggest(truncatedImages || []);
  }, [suggestionImages]);

  const classes = useDefaultStyles(getImageSuggestionCarouselStyles);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSelectImage = useCallback((fullUrl, index, suggestionId) => {
    setBlockCarousel(true);
    addImageCallback([{ fullUrl, suggestionId }], constants.imageOptionKeys.Image)
      .then((response) => {
        if (response.processedImage) {
          if (isCopilotRecommendations) {
            const { idString } = response.processedImage;
            logCopilotViewedImageSuggestions([suggestionId]);
            copilotTracking.trackSelectedImageAssets({ ImageAssets: [{ suggestionId, imageId: idString }] });
          }
        }
        if (response.errorMessage) {
          setCarouselErrorMessage({
            type: MessageBarType.error,
            message: response.errorMessage,
          });
        }
        return Promise.resolve();
      })
      .finally(() => {
        setImagesToSuggest(_.reject(imagesToSuggest, image => image.fullUrl === fullUrl));
        setBlockCarousel(false);
      });
  }, [addImageCallback, copilotTracking, imagesToSuggest, isCopilotRecommendations, logCopilotViewedImageSuggestions]);

  const onAdd = useCallback((images) => {
    setBlockCarousel(true);
    setIsSuggestionsPanelOpen(false);
    addImageCallback(images, constants.imageOptionKeys.Image)
      .then((response) => {
        if (response.processedImages) {
          if (isCopilotRecommendations) {
            response.processedImages.forEach((image) => {
              copilotTracking.trackSelectedImageAssets({ ImageAssets: [{ suggestionId: image.suggestionId, imageId: image.idString }] });
            });
          }
        }
        if (response.errorMessage) {
          setCarouselErrorMessage({
            type: MessageBarType.error,
            message: response.errorMessage,
          });
        }
        return Promise.resolve();
      })
      .finally(() => {
        // setImagesToSuggest(_.reject(imagesToSuggest, image => image.fullUrl === fullUrl));
        setBlockCarousel(false);
      });
  }, [addImageCallback, copilotTracking, isCopilotRecommendations]);

  const onSelectSeeAllImages = useCallback(() => {
    setIsSuggestionsPanelOpen(true);
  }, []);

  const onOffsetChange = useCallback((offset) => {
    const viewedImages = imagesToSuggest.slice(offset, offset + SuggestionViewImageCount);
    const suggestionIds = _.pluck(viewedImages, 'suggestionId');
    reportRecommendations({ view: suggestionIds });
    if (isCopilotRecommendations) {
      logCopilotViewedImageSuggestions(suggestionIds);
    }
  }, [imagesToSuggest, isCopilotRecommendations, logCopilotViewedImageSuggestions, reportRecommendations]);

  const shouldDisplayEmptySuggestionsHint = isEmptySuggestionsHintEnabled && isCopilotRecommendations && !suggestionInProgress && maxUploadNum > 0 && _.isEmpty(imagesToSuggest);

  return (
    <React.Fragment>
      <div className={classes.ImageSuggestionCarouselContainer}>
        {shouldDisplayEmptySuggestionsHint && (
          <MessageBar
            className={classNames('empty-image-suggestions-hint', classes.InfoMessageBar)}
            isMultiline
            messageBarType={MessageBarType.info}
          >
            {customMessageBarText || i18n.getString(_TL_('Select the add image icon above to find relevant stock images or generate custom AI images for your ads.'))
            }
          </MessageBar>
        )}
        <UiBlocker
          blocking={blockCarousel}
          loadingText={i18n.getString(_TL_('Loading'))}
        >
          <ImageSuggestionCarousel
            title={_TL_('Recommended images')}
            helpTopic={isCopilotRecommendations ? 'pop_BA_Recommendations_Pmax_Recommended_Images' : 'pop_BA_Recommendations_Pmax_images'}
            isLoading={suggestionInProgress}
            images={maxUploadNum > 0 ? imagesToSuggest : []}
            i18n={i18n}
            isEmpty={carousel && carousel.isError}
            messageInfo={carouselErrorMessage}
            onSelectImage={onSelectImage}
            onSelectSeeAll={onSelectSeeAllImages}
            showEllipsisWhenNoMoreImages
            showViewAllInHeader={isCopilotRecommendations}
            stylingOverrides={{
              getCarouselStyles,
              getFullyShownImagesCount,
              initialImageContainerLeftMargin,
              seeAllLinkText: _TL_('View all'),
            }}
            onOffsetChange={onOffsetChange}
          />
        </UiBlocker>
      </div>
      {isSuggestionsPanelOpen &&
        <ImageRecommendationsPanel
          i18n={i18n}
          title={_TL_('Recommended images')}
          images={panelState.panelImages}
          themes={themes}
          hasMore={panelState.hasMore}
          loadNextPage={panelState.getMoreImageSuggestions}
          onSelectImage={onSelectImage}
          onDismiss={() => setIsSuggestionsPanelOpen(false)}
          isMultiSelect={isCopilotRecommendations}
          onAdd={onAdd}
          maxUploadNum={maxUploadNum}
        />
      }
    </React.Fragment>);
};

PerfMaxImageSuggestionCarouselInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  addImageCallback: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      DisplayPlusCopilotRecommendations: PropTypes.bool,
    }),
  }).isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  isCopilotRecommendations: PropTypes.bool,
  customMessageBarText: PropTypes.string,
};

PerfMaxImageSuggestionCarouselInternal.defaultProps = {
  isCopilotRecommendations: true,
};

export const PerfMaxImageSuggestionCarousel = withPermissions(withInstrumentation(PerfMaxImageSuggestionCarouselInternal));
